// types/api.js - API контракт и типы данных

// =============================================================================
// ПОЛЬЗОВАТЕЛЬ
// =============================================================================

/**
 * Модель пользователя
 */
export const UserModel = {
  id: '', // string - уникальный ID пользователя
  email: '', // string - email пользователя
  createdAt: '', // string (ISO date) - дата регистрации
  hasContract: false // boolean - есть ли у пользователя договор
};

/**
 * Данные для входа пользователя
 */
export const LoginRequest = {
  email: '', // string - email
  password: '' // string - пароль
};

/**
 * Данные для регистрации пользователя
 */
export const RegisterRequest = {
  email: '', // string - email
  password: '' // string - пароль
};

/**
 * Ответ при успешной аутентификации
 */
export const AuthResponse = {
  user: UserModel, // объект пользователя
  token: '', // string - JWT токен
  expiresIn: 0 // number - время жизни токена в секундах
};

// =============================================================================
// ДОГОВОР
// =============================================================================

/**
 * Модель договора
 */
export const ContractModel = {
  id: '', // string - уникальный ID договора
  userId: '', // string - ID пользователя-владельца
  data: {}, // object - данные договора (contractData из текущего приложения)
  lastModified: '', // string (ISO date) - дата последнего изменения
  createdAt: '', // string (ISO date) - дата создания
  status: 'draft' // 'draft' | 'completed' - статус договора
};

/**
 * Запрос на сохранение договора
 */
export const SaveContractRequest = {
  data: {}, // object - данные договора
  status: 'draft' // 'draft' | 'completed' - статус
};

/**
 * Запрос на экспорт договора
 */
export const ExportContractRequest = {
  format: 'word', // 'word' | 'pdf' - формат экспорта
  contractId: '' // string - ID договора для экспорта
};

// =============================================================================
// API ENDPOINTS
// =============================================================================

/**
 * Конфигурация API endpoints
 */
export const API_ENDPOINTS = {
  // Аутентификация
  LOGIN: '/api/auth/login',
  REGISTER: '/api/auth/register',
  LOGOUT: '/api/auth/logout',
  PROFILE: '/api/auth/profile',
  REFRESH_TOKEN: '/api/auth/refresh',
  
  // Договоры
  GET_CONTRACT: '/api/contract',
  SAVE_CONTRACT: '/api/contract',
  DELETE_CONTRACT: '/api/contract',
  EXPORT_CONTRACT: '/api/contract/export'
};

// =============================================================================
// HTTP МЕТОДЫ
// =============================================================================

/**
 * Методы HTTP запросов для каждого endpoint
 */
export const API_METHODS = {
  [API_ENDPOINTS.LOGIN]: 'POST',
  [API_ENDPOINTS.REGISTER]: 'POST',
  [API_ENDPOINTS.LOGOUT]: 'POST',
  [API_ENDPOINTS.PROFILE]: 'GET',
  [API_ENDPOINTS.REFRESH_TOKEN]: 'POST',
  
  [API_ENDPOINTS.GET_CONTRACT]: 'GET',
  [API_ENDPOINTS.SAVE_CONTRACT]: 'PUT',
  [API_ENDPOINTS.DELETE_CONTRACT]: 'DELETE',
  [API_ENDPOINTS.EXPORT_CONTRACT]: 'POST'
};

// =============================================================================
// СТАТУСЫ ОТВЕТОВ
// =============================================================================

/**
 * Стандартные статусы ответов API
 */
export const API_STATUS = {
  SUCCESS: 'success',
  ERROR: 'error',
  LOADING: 'loading'
};

/**
 * HTTP статус коды
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  INTERNAL_SERVER_ERROR: 500
};

// =============================================================================
// ОШИБКИ
// =============================================================================

/**
 * Типы ошибок API
 */
export const API_ERRORS = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  AUTH_ERROR: 'AUTH_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

/**
 * Модель ошибки API
 */
export const ApiError = {
  type: '', // string - тип ошибки из API_ERRORS
  message: '', // string - сообщение об ошибке
  details: {}, // object - дополнительные детали ошибки
  timestamp: '' // string (ISO date) - время возникновения ошибки
};

// =============================================================================
// СТАНДАРТНЫЙ ОТВЕТ API
// =============================================================================

/**
 * Стандартная структура ответа API
 */
export const ApiResponse = {
  status: API_STATUS.SUCCESS, // success | error | loading
  data: null, // any - данные ответа
  error: null, // ApiError | null - ошибка если есть
  message: '', // string - сообщение
  timestamp: '' // string (ISO date) - время ответа
};

// =============================================================================
// ВАЛИДАЦИЯ
// =============================================================================

/**
 * Правила валидации для различных полей
 */
export const VALIDATION_RULES = {
  EMAIL: {
    required: true,
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    message: 'Введите корректный email адрес'
  },
  PASSWORD: {
    required: true,
    minLength: 6,
    message: 'Пароль должен содержать минимум 6 символов'
  }
};

// =============================================================================
// КОНСТАНТЫ
// =============================================================================

/**
 * Общие константы для работы с API
 */
export const API_CONSTANTS = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:3001',
  TIMEOUT: 10000, // 10 секунд
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000, // 1 секунда
  
  // Настройки автосохранения
  AUTOSAVE_DELAY: 2000, // 2 секунды
  AUTOSAVE_DEBOUNCE: 500, // 0.5 секунды
  
  // Настройки токена
  TOKEN_KEY: 'auth_token',
  TOKEN_EXPIRY_KEY: 'auth_token_expiry',
  USER_KEY: 'auth_user',
  
  // Настройки заглушек
  MOCK_DELAY_MIN: 100, // минимальная задержка заглушки (мс)
  MOCK_DELAY_MAX: 500, // максимальная задержка заглушки (мс)
  MOCK_ERROR_RATE: 0.1 // вероятность ошибки в заглушках (10%)
};

// =============================================================================
// ЭКСПОРТ ПО УМОЛЧАНИЮ
// =============================================================================

export default {
  UserModel,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  ContractModel,
  SaveContractRequest,
  ExportContractRequest,
  API_ENDPOINTS,
  API_METHODS,
  API_STATUS,
  HTTP_STATUS,
  API_ERRORS,
  ApiError,
  ApiResponse,
  VALIDATION_RULES,
  API_CONSTANTS
};